import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  CreateDraftRequest,
  CreateDraftResponse,
  DeleteDraftRequest,
  FinalizeVersionRequest,
  FinalizeVersionResponse,
  ListDraftsResponse,
  ListVersionsResponse,
  PinVersionRequest,
  PinVersionResponse,
  RegenerateChangelogRequest,
  UpdateVersionChangelogRequest,
  WorkflowDefinitionResponse,
} from '@erp/rpc';

/**
 * Version history for a workflow lineage. Returns every version (draft + finalized)
 * ordered by version number descending, with creator name resolved for display.
 *
 * Uses the orpc-keyed cache so it shares state with the route loader's
 * `ensureQueryData(orpc.workflows.listVersions.queryOptions(...))` call —
 * mutations only need to invalidate one key for both consumers to see fresh
 * data.
 */
export function useListWorkflowVersions(workflowId?: string) {
  return useQuery<ListVersionsResponse>({
    ...orpc.workflows.listVersions.queryOptions({
      input: { workflowId: workflowId ?? '' },
    }),
    enabled: !!workflowId,
  });
}

function listVersionsQueryKey(workflowId: string) {
  return orpc.workflows.listVersions.queryOptions({ input: { workflowId } })
    .queryKey;
}

/**
 * Drafts — defaults to the current user's drafts; pass `ownedByCurrentUser: false`
 * to see all drafts in the org (for admin/triage views).
 */
export function useListWorkflowDrafts(params?: {
  ownedByCurrentUser?: boolean;
}) {
  return useQuery<ListDraftsResponse>({
    queryKey: ['workflow-drafts', params],
    queryFn: async () => {
      return await rpcClient.workflows.listDrafts({
        ownedByCurrentUser: params?.ownedByCurrentUser,
      });
    },
  });
}

export function useCreateDraft() {
  const queryClient = useQueryClient();
  return useMutation<CreateDraftResponse, Error, CreateDraftRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.createDraft(input);
    },
    onSuccess: async (data) => {
      // Force a refetch (not just invalidate) before resolving — callers
      // immediately navigate to /v/$newVersion, and the route loader's
      // `ensureQueryData(listVersions)` returns stale cached data unless the
      // refetch finishes first. Without this, the loader can't find the
      // brand-new version and renders "Failed to load workflow definition".
      await queryClient.refetchQueries({
        queryKey: listVersionsQueryKey(data.definition.workflowId),
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-drafts'] });
    },
  });
}

export function useFinalizeVersion() {
  const queryClient = useQueryClient();
  return useMutation<FinalizeVersionResponse, Error, FinalizeVersionRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.finalizeVersion(input);
    },
    onSuccess: async (response) => {
      if (!response.ok) return;
      // Seed the orpc-keyed cache with the just-finalized definition so the
      // route loader's `ensureQueryData` reads fresh state without a network
      // round-trip. (ensureQueryData returns cached data even when stale, so
      // a plain invalidate wouldn't force a re-fetch before the loader runs.)
      queryClient.setQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: response.definition.id },
        }).queryKey,
        response.definition,
      );
      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(response.definition.workflowId),
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-drafts'] });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

export function usePinVersion() {
  const queryClient = useQueryClient();
  return useMutation<PinVersionResponse, Error, PinVersionRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.pinVersion(input);
    },
    onSuccess: async (response) => {
      if (!response.ok) return;
      const workflowId = response.definition.workflowId;

      // The pin changes `workflow.pinnedVersionId` AND flips `isPinned` on
      // (at least) two definition rows — the previously-pinned and the newly-
      // pinned. We don't have the fresh workflow row in the response, so we
      // can't `setQueryData` it directly. Force an awaited refetch so by the
      // time this handler resolves, the route loader's next `ensureQueryData`
      // call sees the new `pinnedVersionId` (otherwise the bare
      // `/workflows/$id` URL resolves to the OLD pinned version after pin).
      await queryClient.refetchQueries({
        queryKey: orpc.workflows.getWorkflow.queryOptions({
          input: { workflowId },
        }).queryKey,
      });

      // Seed the newly-pinned definition's cache with `isPinned: true` so
      // detail pages render it correctly without waiting on another fetch.
      queryClient.setQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: response.definition.id },
        }).queryKey,
        response.definition,
      );

      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(workflowId),
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

export function useDeleteDraft() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, DeleteDraftRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.deleteDraft(input);
    },
    onSuccess: () => {
      // The response doesn't carry the lineageId, so blow away every versions
      // cache entry and the drafts list. Cheap enough for a rare action.
      queryClient.invalidateQueries({
        queryKey: orpc.workflows.listVersions.key(),
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-drafts'] });
    },
  });
}

export function useRegenerateChangelog() {
  const queryClient = useQueryClient();
  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    RegenerateChangelogRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.regenerateChangelog(input);
    },
    onSuccess: (definition) => {
      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(definition.workflowId),
      });
    },
  });
}

export function useUpdateVersionChangelog() {
  const queryClient = useQueryClient();
  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    UpdateVersionChangelogRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.updateVersionChangelog(input);
    },
    onSuccess: (definition) => {
      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(definition.workflowId),
      });
    },
  });
}
